/**
 * Epson ePOS direct printing via HTTP.
 *
 * Sends ePOS XML commands directly to an Epson TM printer's built-in
 * web server. Works from any browser — no drivers, no Tauri, no SDK.
 *
 * Compatible with: TM-T20, TM-T88, TM-M30, TM-L90, and other
 * ePOS-enabled Epson TM printers.
 */

import {
  TicketData,
  LabelData,
  FiscalIssuer,
  FiscalTransparency,
} from "@/types/printer";

// ---------------------------------------------------------------------------
// XML builder helpers
// ---------------------------------------------------------------------------

const esc = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

function formatPrice(value: number): string {
  const formatted = value.toFixed(2).replace(".", ",");
  return `$ ${formatted}`;
}

function pad(left: string, right: string, width: number): string {
  const gap = Math.max(1, width - left.length - right.length);
  return left + " ".repeat(gap) + right;
}

function separator(ch: string, width: number): string {
  return ch.repeat(width);
}

function truncate(s: string, max: number): string {
  if (s.length > max) return s.substring(0, max - 3) + "...";
  return s;
}

const WIDTH = 48;

/** `K7M4RQ` → `K7M-4RQ`: se guarda sin guión y se dicta con guión. */
const formatPickupCode = (code: string): string =>
  code.length === 6 ? `${code.slice(0, 3)}-${code.slice(3)}` : code;

/**
 * Un monto del bloque de transparencia: core lo manda como string decimal con
 * punto ("173.55"). `Number` lee el punto como decimal; si no es un número, se
 * imprime como vino en lugar de un "NaN".
 */
function formatDecimalAmount(value: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? formatPrice(n) : value;
}

/** Rótulo y monto en un renglón; si no entran en el papel, el monto baja. */
function amountLines(label: string, value: string): string[] {
  if (label.length + 1 + value.length <= WIDTH)
    return [pad(label, value, WIDTH)];
  return [label, pad("", value, WIDTH)];
}

/**
 * El emisor (RG 1415, Anexo II, en su orden): razón social, domicilio, CUIT,
 * ingresos brutos, inicio de actividades y condición frente al IVA. Lo que el
 * negocio no cargó no tiene renglón.
 */
function issuerLines(issuer: FiscalIssuer): string[] {
  const lines: string[] = [];
  if (issuer.legalName) lines.push(`Razon social: ${issuer.legalName}`);
  if (issuer.address) lines.push(`Domicilio: ${issuer.address}`);
  if (issuer.cuit) lines.push(`CUIT: ${issuer.cuit}`);
  if (issuer.grossIncomeNumber) {
    lines.push(`Ing. Brutos: ${issuer.grossIncomeNumber}`);
  }
  if (issuer.activityStartDate) {
    lines.push(`Inicio de actividades: ${issuer.activityStartDate}`);
  }
  if (issuer.ivaCondition) lines.push(issuer.ivaCondition);
  return lines;
}

/** Ley 27.743: el IVA contenido y los otros impuestos nacionales indirectos. */
function transparencyLines(t: FiscalTransparency): string[] {
  return [
    "Regimen de Transparencia Fiscal al Consumidor",
    "(Ley 27.743)",
    ...amountLines("IVA Contenido", formatDecimalAmount(t.ivaContenido)),
    ...amountLines(
      "Otros Impuestos Nacionales Indirectos",
      formatDecimalAmount(t.otrosImpuestosNacionalesIndirectos)
    ),
  ];
}

// ---------------------------------------------------------------------------
// ePOS XML envelope
// ---------------------------------------------------------------------------

function wrapEnvelope(commands: string): string {
  return `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
  <s:Body>
    <epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">
${commands}
    </epos-print>
  </s:Body>
</s:Envelope>`;
}

// ---------------------------------------------------------------------------
// Ticket rendering → ePOS XML
// ---------------------------------------------------------------------------

export function renderTicketXml(data: TicketData): string {
  const lines: string[] = [];

  const cmd = (tag: string) => lines.push(`      ${tag}`);

  // Reset
  cmd('<text lang="en"/>');

  // Header
  cmd('<text align="center"/>');
  cmd(`<text>${esc(separator("=", WIDTH))}&#10;</text>`);
  cmd('<text dw="true" dh="true" em="true"/>');
  cmd(`<text>${esc(data.businessName.toUpperCase())}&#10;</text>`);
  cmd('<text dw="false" dh="false" em="false"/>');
  cmd(`<text>${esc(separator("=", WIDTH))}&#10;</text>`);

  // Address
  if (data.localAddress) {
    cmd(`<text>Dir: ${esc(data.localAddress)}&#10;</text>`);
  }

  // Emisor del comprobante: arriba, como en todo ticket fiscal.
  if (data.fiscalData?.issuer) {
    for (const line of issuerLines(data.fiscalData.issuer)) {
      cmd(`<text>${esc(line)}&#10;</text>`);
    }
  }

  // Info
  cmd('<text align="left"/>');
  cmd(`<text>${esc(separator("-", WIDTH))}&#10;</text>`);
  cmd(`<text>Fecha: ${esc(data.date || "---")}&#10;</text>`);
  if (data.showSeller !== false) {
    cmd(`<text>Vendedor: ${esc(data.sellerName)}&#10;</text>`);
  }
  if (data.buyerName) {
    cmd(`<text>Cliente: ${esc(data.buyerName)}&#10;</text>`);
  }
  if (data.orderId) {
    cmd(`<text>Orden: ${esc(data.orderId)}&#10;</text>`);
  }

  // Products
  cmd(`<text>${esc(separator("-", WIDTH))}&#10;</text>`);
  cmd('<text em="true"/>');
  cmd("<text>PRODUCTOS:&#10;</text>");
  cmd('<text em="false"/>');

  for (const p of data.products) {
    const qty = `${p.quantity}x`;
    const name = truncate(p.name, 26);
    const price = formatPrice(p.price);
    cmd(`<text>${esc(pad(`${qty} ${name}`, price, WIDTH))}&#10;</text>`);
  }

  for (const p of data.looseProducts) {
    const qty = `${p.quantity}${p.unitType}`;
    const name = truncate(p.name, 24);
    const price = formatPrice(p.price);
    cmd(`<text>${esc(pad(`${qty} ${name}`, price, WIDTH))}&#10;</text>`);
  }

  // Totals
  cmd(`<text>${esc(separator("-", WIDTH))}&#10;</text>`);
  cmd(
    `<text>${esc(
      pad("Subtotal:", formatPrice(data.subtotal), WIDTH)
    )}&#10;</text>`
  );

  if (data.discounts && data.discounts > 0) {
    cmd(
      `<text>${esc(
        pad("Descuentos:", `-${formatPrice(data.discounts)}`, WIDTH)
      )}&#10;</text>`
    );
  }
  if (data.appliedCouponsAmount && data.appliedCouponsAmount > 0) {
    cmd(
      `<text>${esc(
        pad("Cupones:", `-${formatPrice(data.appliedCouponsAmount)}`, WIDTH)
      )}&#10;</text>`
    );
  }
  if (data.appliedOffersAmount && data.appliedOffersAmount > 0) {
    cmd(
      `<text>${esc(
        pad("Ofertas:", `-${formatPrice(data.appliedOffersAmount)}`, WIDTH)
      )}&#10;</text>`
    );
  }

  // Grand total
  cmd(`<text>${esc(separator("-", WIDTH))}&#10;</text>`);
  cmd('<text dw="true" dh="true" em="true"/>');
  cmd(`<text>${esc(pad("TOTAL:", formatPrice(data.total), 24))}&#10;</text>`);
  cmd('<text dw="false" dh="false" em="false"/>');

  // Transparencia fiscal: va junto al total, que es lo que discrimina.
  if (data.fiscalData?.transparency) {
    cmd(`<text>${esc(separator("-", WIDTH))}&#10;</text>`);
    for (const line of transparencyLines(data.fiscalData.transparency)) {
      cmd(`<text>${esc(line)}&#10;</text>`);
    }
  }

  // Payment
  cmd(`<text>${esc(separator("=", WIDTH))}&#10;</text>`);
  cmd('<text align="center"/>');
  const methods = data.paymentMethods.map((m) => m.name).join(", ");
  cmd(`<text>Metodo: ${esc(methods)}&#10;</text>`);

  // Fiscal data
  if (data.fiscalData) {
    const f = data.fiscalData;
    cmd(`<text>${esc(separator("=", WIDTH))}&#10;</text>`);
    cmd('<text align="center"/>');
    cmd('<text em="true"/>');
    cmd("<text>COMPROBANTE FISCAL&#10;</text>");
    cmd('<text em="false"/>');
    cmd('<text align="left"/>');
    cmd(`<text>Tipo: Factura ${esc(f.invoiceType)}&#10;</text>`);
    if (f.invoiceNumber) {
      cmd(`<text>Nro: ${esc(f.invoiceNumber)}&#10;</text>`);
    }
    cmd(`<text>CAE: ${esc(f.cae)}&#10;</text>`);
    if (f.caeExpiration) {
      cmd(`<text>Vto CAE: ${esc(f.caeExpiration)}&#10;</text>`);
    }
    cmd('<text align="center"/>');
    // QR oficial de AFIP si está disponible; si no, fallback al CAE en code128.
    if (f.qrData) {
      cmd(
        `<symbol type="qrcode_model_2" level="level_m" width="5" height="5">${esc(
          f.qrData
        )}</symbol>`
      );
    } else {
      cmd(
        `<barcode type="code128" hri="below" height="50" width="2">${esc(
          f.cae
        )}</barcode>`
      );
    }
  }

  // Retiro en otro local: lo que el cliente NO se lleva puesto, y el código con
  // el que lo reclama. Va antes del pie porque es lo último que tiene que leer.
  const pickupProducts = (data.products ?? []).filter(
    (product) => !!product.pickupWarehouseName
  );
  if (data.pickupCode && pickupProducts.length > 0) {
    cmd(`<text>${esc(separator("=", WIDTH))}&#10;</text>`);
    cmd('<text align="center"/>');
    cmd('<text em="true"/>');
    cmd("<text>RETIRAR EN OTRO LOCAL&#10;</text>");
    cmd('<text em="false"/>');
    cmd('<text dw="true" dh="true" em="true"/>');
    cmd(`<text>${esc(formatPickupCode(data.pickupCode))}&#10;</text>`);
    cmd('<text dw="false" dh="false" em="false"/>');
    cmd('<text align="left"/>');
    for (const product of pickupProducts) {
      cmd(`<text>${esc(`${product.quantity} x ${product.name}`)}&#10;</text>`);
      cmd(`<text>${esc(`  -> ${product.pickupWarehouseName}`)}&#10;</text>`);
      // La dirección en su propio renglón: pegada al nombre se pasa de los 48
      // caracteres del papel y la impresora la corta.
      if (product.pickupWarehouseAddress) {
        cmd(
          `<text>${esc(`     ${product.pickupWarehouseAddress}`)}&#10;</text>`
        );
      }
    }
    cmd('<text align="center"/>');
    cmd("<text>Presenta este codigo para retirarlo&#10;</text>");
  }

  // Footer
  cmd(`<text>${esc(separator("=", WIDTH))}&#10;</text>`);
  cmd('<text align="center"/>');
  cmd('<text em="true"/>');
  cmd("<text>Gracias por su compra!&#10;</text>");
  cmd('<text em="false"/>');

  if (!data.fiscalData) {
    cmd("<text>* Comprobante sin validez fiscal&#10;</text>");
  }

  cmd(`<text>${esc(separator("=", WIDTH))}&#10;</text>`);
  cmd('<feed line="3"/>');
  cmd('<cut type="feed"/>');

  return wrapEnvelope(lines.join("\n"));
}

// ---------------------------------------------------------------------------
// Label rendering → ePOS XML
// ---------------------------------------------------------------------------

export function renderLabelXml(label: LabelData): string {
  const lines: string[] = [];
  const cmd = (tag: string) => lines.push(`      ${tag}`);
  const isLoose = !!label.unitPrice || !!label.unitType;

  cmd('<text lang="en"/>');
  cmd('<text align="center"/>');

  // Business header
  if (label.businessName) {
    cmd(`<text>${esc(label.businessName)}&#10;</text>`);
    if (label.businessAddress) {
      cmd(`<text>${esc(label.businessAddress)}&#10;</text>`);
    }
    cmd(`<text>${esc(separator("-", WIDTH))}&#10;</text>`);
  }

  // Product name
  cmd('<text dw="true" em="true"/>');
  cmd(`<text>${esc(truncate(label.productName, 24))}&#10;</text>`);
  cmd('<text dw="false" em="false"/>');

  if (label.variant) {
    cmd(`<text>${esc(label.variant)}&#10;</text>`);
  }

  if (isLoose) {
    const unitType = label.unitType || "kg";
    if (label.unitPrice) {
      cmd('<text align="left"/>');
      cmd(
        `<text>${esc(
          pad(`$/${unitType}`, formatPrice(label.unitPrice), WIDTH)
        )}&#10;</text>`
      );
      cmd('<text align="center"/>');
    }
  }

  if (label.sku) {
    cmd(`<text>${isLoose ? "PLU" : "SKU"}: ${esc(label.sku)}&#10;</text>`);
  }

  // Barcode
  cmd('<feed line="1"/>');
  cmd(
    `<barcode type="code128" hri="below" height="60" width="2">${esc(
      label.barcode
    )}</barcode>`
  );
  cmd('<feed line="1"/>');

  // Price
  if (label.price != null) {
    if (isLoose) {
      cmd(`<text>${esc(separator("=", WIDTH))}&#10;</text>`);
      cmd('<text em="true"/>');
      cmd("<text>IMPORTE $&#10;</text>");
    }
    cmd('<text dw="true" dh="true" em="true"/>');
    cmd(`<text>${esc(formatPrice(label.price))}&#10;</text>`);
    cmd('<text dw="false" dh="false" em="false"/>');
  }

  cmd('<feed line="2"/>');
  cmd('<cut type="feed"/>');

  return wrapEnvelope(lines.join("\n"));
}

// ---------------------------------------------------------------------------
// Send XML to the printer's HTTP endpoint
// ---------------------------------------------------------------------------

export async function sendEposXml(
  host: string,
  port: number,
  xml: string
): Promise<{ success: boolean; error?: string }> {
  const url = `http://${host}:${port}/cgi-bin/epos/service.cgi`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "text/xml; charset=utf-8",
        SOAPAction: '""',
      },
      body: xml,
    });

    if (!response.ok) {
      return {
        success: false,
        error: `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    const text = await response.text();
    // Check for success in response XML
    if (text.includes('success="true"') || text.includes("response")) {
      return { success: true };
    }

    return { success: false, error: text };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : "Error de conexion",
    };
  }
}

// ---------------------------------------------------------------------------
// High-level helpers
// ---------------------------------------------------------------------------

export async function epsonPrintTicket(
  host: string,
  port: number,
  data: TicketData
): Promise<{ success: boolean; error?: string }> {
  const xml = renderTicketXml(data);
  return sendEposXml(host, port, xml);
}

export async function epsonPrintLabel(
  host: string,
  port: number,
  label: LabelData,
  copies: number = 1
): Promise<{ success: boolean; error?: string }> {
  // Send each copy sequentially
  for (let i = 0; i < copies; i++) {
    const xml = renderLabelXml(label);
    const result = await sendEposXml(host, port, xml);
    if (!result.success) return result;
  }
  return { success: true };
}
